/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The ONE place the comparer's URL rules live: what `?audience=`, `?volume=`
 * and `?contractDuration=` mean, and the link that carries them from the
 * results to /vergelijk. The homepage comparer and the shortlist page both
 * read an address through these functions, so a shared link means the same on
 * both and the figures on /vergelijk are computed from the inputs the rows
 * were.
 *
 * Extracted rule for rule from ComparisonEngine.tsx, quirks included, because
 * an address that is already out there must keep meaning what it meant:
 *
 *   audience          whitelisted; anything off the list (a typo,
 *                     'particulier', a stale link) falls back to
 *                     'Particulier'. It is compared straight against
 *                     `provider.targetAudience`, so a value that is merely
 *                     unknown would match no row and leave an empty comparer
 *                     with every control looking untouched.
 *   volume            `parseInt` of the parameter; absent, unparseable or 0
 *                     falls back to the audience's default
 *                     (src/lib/earnings.ts, `defaultAnnualKwh`). The fallback
 *                     follows the audience that was READ, not the raw
 *                     parameter; a typed volume stays whatever was typed.
 *   contractDuration  the parameter as given, '1 jaar' when absent
 *                     (DEFAULT_DURATION_SELECTION: every looptijd, 'Alle',
 *                     from 2026-10-16 to 2026-10-30). 'Alle', one looptijd or
 *                     a comma list of them (src/lib/durationOptions.ts, the
 *                     multi-select). No whitelist (the engine has none either).
 *
 * Data identifiers, never labels: 'Alle', 'Particulier', 'Zakelijk' and
 * '1 jaar' are matched against the provider database, so they stay Dutch in
 * every language (CLAUDE.md, "URL param values"). Nothing here formats a
 * number for a reader; the volume is plain digits in the address.
 *
 * Pure on purpose (the providerFeatures.ts pattern): no React, no router, no
 * i18n. `URLSearchParams` in, plain values out, so both pages, the tests and
 * anything that bakes a link call the same functions.
 */

import { defaultAnnualKwh } from './earnings';
import { DEFAULT_DURATION_SELECTION, parseDurationSelection } from './durationOptions';
import { VOLUME_MANUAL_MAX } from './kwhEstimate';

/** Also the URL allowlist for `?audience=`. */
export const AUDIENCE_VALUES = ['Alle', 'Particulier', 'Zakelijk'] as const;
export type AudienceValue = (typeof AUDIENCE_VALUES)[number];

const DEFAULT_AUDIENCE: AudienceValue = 'Particulier';
const DEFAULT_CONTRACT_DURATION = DEFAULT_DURATION_SELECTION;

const isAudienceValue = (value: string | null | undefined): value is AudienceValue =>
  typeof value === 'string' && (AUDIENCE_VALUES as readonly string[]).includes(value);

/** Whitelisted, not trusted: anything off `AUDIENCE_VALUES` is 'Particulier'. */
export function parseAudience(raw: string | null | undefined): AudienceValue {
  return isAudienceValue(raw) ? raw : DEFAULT_AUDIENCE;
}

/**
 * The volume in kWh per year. Absent, unparseable and 0 all fall through to
 * the audience's default.
 *
 * `parseInt` without a radix, `0` and NaN both falling through `||`, and a
 * negative number surviving, are the engine's own behaviour and are mirrored
 * on purpose: this module changes where the rule lives, not what it says. A
 * stricter rule (no negatives, a ceiling) is a decision of its own; make it
 * here, once, and both pages follow.
 */
export function parseVolume(raw: string | null | undefined, audience: AudienceValue): number {
  const fallback = defaultAnnualKwh(audience);
  return parseInt(raw || String(fallback)) || fallback;
}

/**
 * The volume the VISITOR set, or null when the comparer is on its default.
 * `parseVolume` cannot tell the two apart (a missing parameter and a typed
 * 2 500 read the same); the signup wizard has to, because it records whether
 * the kWh it prefills was ever the visitor's own number. Only what
 * `parseVolume` would take as typed counts: a positive whole number of at
 * most the comparer's typed-volume cap.
 */
export function explicitComparerVolume(params: URLSearchParams): number | null {
  const n = parseInt(params.get('volume') || '');
  return Number.isSafeInteger(n) && n >= 1 && n <= VOLUME_MANUAL_MAX ? n : null;
}

/** Whatever was given, the default looptijd ('1 jaar') when nothing was. */
export function parseContractDuration(raw: string | null | undefined): string {
  return raw || DEFAULT_CONTRACT_DURATION;
}

/** The three inputs of Stap 1 (Bereken), as an address carries them. */
export interface ComparerInputs {
  audience: AudienceValue;
  volume: number;
  contractDuration: string;
}

/** The audience is read first: the volume's fallback depends on it. */
export function readComparerInputs(params: URLSearchParams): ComparerInputs {
  const audience = parseAudience(params.get('audience'));
  return {
    audience,
    volume: parseVolume(params.get('volume'), audience),
    contractDuration: parseContractDuration(params.get('contractDuration')),
  };
}

/**
 * The link from the comparer's shortlist to /vergelijk, for example
 * `/nl/vergelijk?ids=b-2,a-1&audience=Zakelijk&volume=5000&contractDuration=1+jaar`.
 *
 * - `ids` comes first, in the order the visitor ticked them, as a literal
 *   comma list: each slug is escaped on its own, so nothing in a slug can end
 *   the list or open a parameter, while the separator stays readable in the
 *   address bar. The page splits `ids` on commas after `URLSearchParams` has
 *   decoded it, so an escaped comma (`%2C`) would read the same.
 * - The other three are written with `URLSearchParams`, so a space is `+`
 *   (`1+jaar`). The reader is `URLSearchParams` too (`useSearchParams`,
 *   `readComparerInputs`), which turns `+` and `%20` back into a space.
 * - `contractDuration` is carried only when exactly ONE looptijd is selected.
 *   /vergelijk shows ONE row per provider and prefers one looptijd; 'Alle' and
 *   a list of several have no single preference there, and without the
 *   parameter the page falls back to its own (the 1-year row). The audience
 *   has no such rule: 'Alle' stays in the link.
 */
export function buildShortlistHref(
  language: 'nl' | 'en',
  slugs: readonly string[],
  inputs: ComparerInputs
): string {
  const params = new URLSearchParams();
  params.set('audience', inputs.audience);
  params.set('volume', String(inputs.volume));
  const durations = parseDurationSelection(inputs.contractDuration);
  if (durations?.length === 1) params.set('contractDuration', durations[0]);
  const ids = slugs.map((slug) => encodeURIComponent(slug)).join(',');
  return `/${language}/vergelijk?ids=${ids}&${params.toString()}`;
}
